"use client";

import type * as React from "react";
import { Toaster as Sonner } from "sonner";

import { cn } from "@/lib/utils";
import { APP_TOASTER_PROPS } from "./toaster-props";

type ToasterProps = Omit<React.ComponentProps<typeof Sonner>, "richColors">;

const baseToast = [
  "grid grid-cols-[auto_1fr_auto_auto_auto] items-center justify-items-start gap-2",
  "min-w-[240px] max-w-[480px] px-3 py-2",
  "rounded-md border border-border shadow-lg",
  "!text-left",
].join(" ");
const baseButtons = "h-7 rounded-sm px-2 text-xs";
const neutralToast = [baseToast, "bg-background text-foreground"].join(" ");

const baseElementClasses = {
  icon: "col-start-1 row-start-1 grid size-4 place-items-center",
  content: "col-start-2 row-start-1 justify-self-start !text-left",
  title: "text-sm font-medium !text-left",
  description: "text-xs text-muted-foreground !text-left",
  loader:
    "!static !top-auto !left-auto !translate-x-0 !translate-y-0 col-start-1 row-start-1 size-4 place-self-center",
  closeButton:
    "col-start-5 row-start-1 grid size-5 justify-self-end place-items-center rounded-sm bg-transparent p-0 opacity-70 hover:opacity-100",
} as const;

const toastClassNames = {
  toast: neutralToast,
  default: neutralToast,
  loading: neutralToast,
  success: neutralToast,
  error: neutralToast,
  warning: neutralToast,
  info: neutralToast,
  ...baseElementClasses,
  actionButton: `${baseButtons} col-start-3 row-start-1 ml-2 justify-self-end bg-background text-foreground`,
  cancelButton: `${baseButtons} col-start-4 row-start-1 justify-self-end bg-background text-foreground/80`,
};

const toastIcons = {
  success: (
    <span aria-hidden="true" className="size-2 rounded-full bg-badge-green" />
  ),
  info: (
    <span aria-hidden="true" className="size-2 rounded-full bg-badge-sky" />
  ),
  warning: (
    <span aria-hidden="true" className="size-2 rounded-full bg-badge-amber" />
  ),
  error: (
    <span aria-hidden="true" className="size-2 rounded-full bg-badge-red" />
  ),
  loading: (
    <span
      aria-hidden="true"
      className="size-2 animate-pulse rounded-full bg-muted-foreground"
    />
  ),
};

function Toaster({ className, ...props }: ToasterProps) {
  const toastOptions = {
    ...props.toastOptions,
    unstyled: true,
    classNames: toastClassNames,
    style: {
      textAlign: "left",
      direction: "ltr",
      ...(props.toastOptions?.style ?? {}),
    },
  } as const;

  return (
    <Sonner
      className={cn("toaster group", className)}
      toastOptions={toastOptions}
      richColors={false}
      icons={toastIcons}
      expand={props.expand ?? true}
      duration={props.duration ?? 3500}
      visibleToasts={props.visibleToasts ?? 4}
      gap={props.gap ?? 12}
      {...props}
    />
  );
}

/** sonner's close mark, which the toaster's close button shows. */
const closeIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

/**
 * One toast at rest, as the application's toaster (APP_TOASTER_PROPS) draws
 * a single toast once it has settled: sonner's markup with this toaster's
 * classes, mark and close button, for a surface that shows a toast without
 * raising one, such as a product shot. sonner's stylesheet places it.
 * sonner.test.tsx holds it to the live toaster's markup.
 */
export function ToastView({
  type = "success",
  title,
  description,
}: {
  type?: "success" | "info" | "warning" | "error";
  title: React.ReactNode;
  description?: React.ReactNode;
}) {
  const [y, x] = APP_TOASTER_PROPS.position.split("-");
  const { offset, mobileOffset } = APP_TOASTER_PROPS;
  return (
    <section
      aria-label="Notifications alt+T"
      tabIndex={-1}
      aria-live="polite"
      aria-relevant="additions text"
      aria-atomic="false"
      data-react-aria-top-layer
    >
      <ol
        dir="ltr"
        tabIndex={-1}
        className={cn("toaster group", APP_TOASTER_PROPS.className)}
        data-sonner-toaster
        data-sonner-theme="light"
        data-y-position={y}
        data-x-position={x}
        style={
          {
            "--front-toast-height": "0px",
            "--width": "356px",
            "--gap": "12px",
            "--offset-top": offset.top,
            "--offset-right": "24px",
            "--offset-bottom": "24px",
            "--offset-left": "24px",
            "--mobile-offset-top": mobileOffset.top,
            "--mobile-offset-right": "16px",
            "--mobile-offset-bottom": "16px",
            "--mobile-offset-left": "16px",
          } as React.CSSProperties
        }
      >
        <li
          tabIndex={0}
          // sonner joins the classes as given, repeats included.
          className={[toastClassNames.toast, toastClassNames[type]].join(" ")}
          data-sonner-toast=""
          data-rich-colors="false"
          data-styled="false"
          data-mounted="true"
          data-promise="false"
          data-swiped="false"
          data-removed="false"
          data-visible="true"
          data-y-position={y}
          data-x-position={x}
          data-index="0"
          data-front="true"
          data-swiping="false"
          data-dismissible="true"
          data-type={type}
          data-swipe-out="false"
          data-expanded="true"
          style={
            {
              "--index": 0,
              "--toasts-before": 0,
              "--z-index": 1,
              "--offset": "0px",
              "--initial-height": "auto",
              textAlign: "left",
              direction: "ltr",
            } as React.CSSProperties
          }
        >
          <button
            aria-label="Close toast"
            data-disabled="false"
            data-close-button="true"
            className={toastClassNames.closeButton}
          >
            {closeIcon}
          </button>
          <div data-icon="" className={toastClassNames.icon}>
            {toastIcons[type]}
          </div>
          <div data-content="" className={toastClassNames.content}>
            <div data-title="" className={toastClassNames.title}>
              {title}
            </div>
            {description ? (
              <div data-description="" className={toastClassNames.description}>
                {description}
              </div>
            ) : null}
          </div>
        </li>
      </ol>
    </section>
  );
}

export { toast } from "sonner";
export { Toaster };
